/**
 * useHelpKeyboard Hook
 *
 * React hook for managing keyboard shortcuts for the help overlay.
 * Handles toggling help on/off and closing when control keys are pressed.
 */

import { useEffect, useRef, useCallback } from 'react';
import type { UseHelpKeyboardOptions } from './types';
import { KEYBOARD_KEYS, HELP_TIMING } from './constants';

/**
 * Check if the event target is an editable element (input, textarea, contenteditable)
 */
function isEditableElement(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toLowerCase();
  if (tagName === 'input' || tagName === 'textarea') {
    return true;
  }

  // Check for contenteditable - both the property and attribute
  if (target.isContentEditable || target.getAttribute('contenteditable') === 'true') {
    return true;
  }

  return false;
}

/**
 * useHelpKeyboard Hook
 *
 * Manages keyboard shortcuts for the help overlay:
 * - Toggle help on `?` or `H` key press
 * - Close help on `Escape` key
 * - Close help on control keys (W, A, S, D, +, -, =) when help is open
 * - Ignores keys when user is typing in an input/textarea
 * - Includes debounce to prevent rapid toggle spam
 *
 * @param options - Configuration options
 * @param options.enabled - Whether keyboard shortcuts are enabled
 * @param options.isHelpOpen - Current state of help overlay
 * @param options.onOpen - Callback to open help overlay
 * @param options.onClose - Callback to close help overlay
 *
 * @example
 * ```typescript
 * const [isHelpOpen, setIsHelpOpen] = useState(false);
 *
 * useHelpKeyboard({
 *   enabled: true,
 *   isHelpOpen,
 *   onOpen: () => setIsHelpOpen(true),
 *   onClose: () => setIsHelpOpen(false),
 * });
 * ```
 */
export function useHelpKeyboard(options: UseHelpKeyboardOptions): void {
  const { enabled, isHelpOpen, onOpen, onClose } = options;

  // Track last action time for debouncing
  const lastActionTimeRef = useRef<number>(0);

  // Use refs to always have current values in the event handler
  const isHelpOpenRef = useRef(isHelpOpen);
  const enabledRef = useRef(enabled);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);

  // Keep refs in sync with props
  useEffect(() => {
    isHelpOpenRef.current = isHelpOpen;
  }, [isHelpOpen]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't handle if disabled
    if (!enabledRef.current) {
      return;
    }

    // Don't capture keys when typing in input/textarea/contenteditable
    if (isEditableElement(event.target)) {
      return;
    }

    const { key } = event;
    const currentHelpOpen = isHelpOpenRef.current;

    // Cast arrays to readonly string[] for type-safe includes() check
    const toggleKeys: readonly string[] = KEYBOARD_KEYS.TOGGLE;
    const closeKeys: readonly string[] = KEYBOARD_KEYS.CLOSE;
    const controlKeys: readonly string[] = KEYBOARD_KEYS.CONTROL;

    // Check for toggle keys (?, h, H)
    if (toggleKeys.includes(key)) {
      // Debounce check
      const now = Date.now();
      if (now - lastActionTimeRef.current < HELP_TIMING.TOGGLE_DEBOUNCE) {
        return;
      }
      lastActionTimeRef.current = now;

      if (currentHelpOpen) {
        onCloseRef.current();
      } else {
        onOpenRef.current();
      }
      return;
    }

    // Check for close keys (Escape) - only when help is open
    if (closeKeys.includes(key)) {
      if (currentHelpOpen) {
        // Debounce check
        const now = Date.now();
        if (now - lastActionTimeRef.current < HELP_TIMING.TOGGLE_DEBOUNCE) {
          return;
        }
        lastActionTimeRef.current = now;

        onCloseRef.current();
      }
      return;
    }

    // Check for control keys (WASD, +, -, =) - only close when help is open
    if (controlKeys.includes(key)) {
      if (currentHelpOpen) {
        // Debounce check
        const now = Date.now();
        if (now - lastActionTimeRef.current < HELP_TIMING.TOGGLE_DEBOUNCE) {
          return;
        }
        lastActionTimeRef.current = now;

        onCloseRef.current();
      }
      return;
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}
